import {
  AlignType,
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  EModalVariants,
  ETextStyleVariant,
  GeneralCalendarIcon,
  GeneralCaretRightIcon,
  Heading,
  Modal,
  Selector,
} from '@outdoorsyco/bonfire';
import cn from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { getHeaderSearchAddressFilter } from '@/redux/selectors/globalHeader';
import { ESearchWidgetFilter } from '@/services/analytics/search/types';

import { EHomeSearchWidgetVariant } from './HomeSearchWidget';

type TTripInformationHomeContentProps = {
  variant: EHomeSearchWidgetVariant;
  isOpened: boolean;
  onClose: () => void;
  onSelectFilter: (filter?: ESearchWidgetFilter) => void;
  onSearch: () => void;
  dateButtonLabel: string;
  datesSelection?: string;
  driverAgeButtonLabel: string;
  driverAgeSelection?: '21-24' | '25+' | null;
  guestsButtonLabel: string;
  guestsSelection?: string;
  vehicleTypeButtonLabel: string;
  vehicleTypeSelection?: string;
};

export const TripInformationHomeContent = ({
  variant,
  isOpened,
  onClose,
  onSelectFilter,
  onSearch,
  dateButtonLabel,
  datesSelection,
  guestsButtonLabel,
  guestsSelection,
  driverAgeButtonLabel,
  driverAgeSelection,
  vehicleTypeButtonLabel,
  vehicleTypeSelection,
}: TTripInformationHomeContentProps) => {
  const intl = useIntl();
  const { isAboveDesktop } = useBreakpoint();

  const addressSelection = useSelector(getHeaderSearchAddressFilter);

  const shouldDisplayDriverAgeFilter =
    variant === EHomeSearchWidgetVariant.RvRentals ||
    variant === EHomeSearchWidgetVariant.RvDelivery;

  const getLocationDescription = () => {
    if (addressSelection) return addressSelection;

    return getAddressButtonLabel();
  };

  const getAddressButtonLabel = () => {
    return intl.formatMessage({ defaultMessage: 'Location', id: 'rvirM2' });
  };

  if (isAboveDesktop) {
    return null;
  }

  return (
    <Modal isOpen={isOpened} onClose={onClose} variant={EModalVariants.Bottom} className="z-[1410]">
      <Modal.Actions className="top-5" />
      <Modal.Header inlineHeader={true} className="mb-6 !pt-4 !pb-0">
        <Heading variant={EHeadingStyleVariant.H6}>
          {intl.formatMessage({ defaultMessage: 'Trip information', id: 'MrJVmR' })}
        </Heading>
      </Modal.Header>
      <Modal.Content className="py-2">
        <div className="flex flex-col gap-6">
          <Selector
            name="location"
            ariaLabel={intl.formatMessage({
              defaultMessage: 'Please select a location.',
              id: '1h5k89',
            })}
            label={addressSelection ? getAddressButtonLabel() : ''}
            description={getLocationDescription()}
            value={addressSelection || ''}
            onChange={() => onSelectFilter(ESearchWidgetFilter.Location)}
            contentWrapperClassName={cn('!py-3 !px-4', {
              '!py-5': !addressSelection,
            })}
            descriptionClassName="truncate"
            labelVariant={ETextStyleVariant.SmallBold}
            descriptionVariant={ETextStyleVariant.MediumRegular}
          />
          <Selector
            name="dates"
            ariaLabel={intl.formatMessage({
              defaultMessage: 'Please select some dates.',
              id: '/1Vehk',
            })}
            icon={GeneralCalendarIcon}
            label={intl.formatMessage({
              defaultMessage: 'Dates',
              id: 'D6b0Au',
            })}
            description={dateButtonLabel}
            value={datesSelection || ''}
            onChange={() => onSelectFilter(ESearchWidgetFilter.Date)}
            contentWrapperClassName="!py-3 !px-4 [&>div]:w-full [&>div]:justify-between"
            labelVariant={ETextStyleVariant.SmallBold}
            descriptionVariant={ETextStyleVariant.MediumRegular}
            alignVariant={AlignType.HorizontalRight}
          />
          <Selector
            name="guests"
            ariaLabel={intl.formatMessage({
              defaultMessage: 'Please select some guests.',
              id: 'wGpMIJ',
            })}
            icon={GeneralCaretRightIcon}
            label={intl.formatMessage({ defaultMessage: 'Guests', id: 'NIVTxd' })}
            description={guestsButtonLabel}
            value={guestsSelection || ''}
            onChange={() => onSelectFilter(ESearchWidgetFilter.Guests)}
            contentWrapperClassName="!py-3 !px-4 [&>div]:w-full [&>div]:justify-between"
            labelVariant={ETextStyleVariant.SmallBold}
            descriptionVariant={ETextStyleVariant.MediumRegular}
            alignVariant={AlignType.HorizontalRight}
          />
          {shouldDisplayDriverAgeFilter && (
            <Selector
              name="driverAge"
              ariaLabel={intl.formatMessage({
                defaultMessage: 'Please select a driver age.',
                id: 'ZwSCCg',
              })}
              icon={GeneralCaretRightIcon}
              label={intl.formatMessage({ defaultMessage: 'Driver age', id: 'klaoBH' })}
              description={driverAgeButtonLabel}
              value={driverAgeSelection || ''}
              onChange={() => onSelectFilter(ESearchWidgetFilter.DriverAge)}
              contentWrapperClassName="!py-3 !px-4 [&>div]:w-full [&>div]:justify-between"
              labelVariant={ETextStyleVariant.SmallBold}
              descriptionVariant={ETextStyleVariant.MediumRegular}
              alignVariant={AlignType.HorizontalRight}
            />
          )}
          {variant !== EHomeSearchWidgetVariant.Campgrounds && (
            <Selector
              name="vehicleType"
              ariaLabel={intl.formatMessage({
                defaultMessage: 'Please select a vehicle type.',
                id: 'JaOup5',
              })}
              icon={GeneralCaretRightIcon}
              label={intl.formatMessage({ defaultMessage: 'Rental type', id: 'u1rM7P' })}
              description={vehicleTypeButtonLabel}
              value={vehicleTypeSelection || ''}
              onChange={() => onSelectFilter(ESearchWidgetFilter.VehicleType)}
              contentWrapperClassName="!py-3 !px-4 [&>div]:w-full [&>div]:justify-between"
              labelVariant={ETextStyleVariant.SmallBold}
              descriptionVariant={ETextStyleVariant.MediumRegular}
              alignVariant={AlignType.HorizontalRight}
            />
          )}
        </div>
      </Modal.Content>
      <Modal.Footer className="p-4 !justify-end">
        <Button
          variant={EButtonColorVariant.Primary}
          label={intl.formatMessage({ defaultMessage: 'Search', id: 'xmcVZ0' })}
          onClick={onSearch}
        />
      </Modal.Footer>
    </Modal>
  );
};
